/**
 * Test Call (Wave 6.3): place a live SIP call over WebRTC (WSS) directly to
 * FreeSWITCH, targeting the echo test extension (9196). Verifies the media
 * path (WSS signaling + WebRTC RTP) without touching a carrier.
 *
 * Config via localStorage: fs_wss_uri (e.g. wss://host:7443), set on first use.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";

// sip.js is imported lazily to keep the main bundle lean
import type { UserAgent, Session } from "sip.js";

type CallState = "idle" | "connecting" | "active" | "ended" | "failed";

export default function TestCall() {
  const [wssUri, setWssUri] = useState<string>(
    () => localStorage.getItem("fs_wss_uri") || "wss://" + window.location.hostname + ":7443"
  );
  const [target, setTarget] = useState("9196");
  const [state, setState] = useState<CallState>("idle");
  const [statusLine, setStatusLine] = useState("");
  const [error, setError] = useState("");
  const uaRef = useRef<UserAgent | null>(null);
  const sessionRef = useRef<Session | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      // cleanup on unmount
      try { sessionRef.current?.dispose(); } catch { /* noop */ }
      try { uaRef.current?.stop(); } catch { /* noop */ }
    };
  }, []);

  const makeCall = useCallback(async () => {
    setError("");
    setState("connecting");
    setStatusLine("Connecting to " + wssUri + " …");
    try {
      const { UserAgent: UA, Inviter, Web } = await import("sip.js");

      const testUri = UA.makeURI(`sip:testcall@${window.location.hostname}`);
      if (!testUri) throw new Error("Invalid test call URI");
      const ua = new UA({
        uri: testUri,
        transportOptions: { server: wssUri },
        authorizationUsername: undefined,
        sessionDescriptionHandlerFactory: Web.defaultSessionDescriptionHandlerFactory(),
        logBuiltinEnabled: false,
        logLevel: "error",
      });
      uaRef.current = ua;

      ua.delegate = {
        onDisconnect: (e) => {
          if (state !== "idle") {
            setState("failed");
            setError("WSS disconnected" + (e ? ": " + e : ""));
          }
        },
      };

      await ua.start();
      setStatusLine("Connected. Dialing " + target + " …");

      const targetUri = UA.makeURI(`sip:${target}@${window.location.hostname}`);
      if (!targetUri) throw new Error("Invalid destination URI");
      const inviter = new Inviter(ua, targetUri, {
        sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
      });
      sessionRef.current = inviter;

      inviter.stateChange.addListener((s) => {
        if (s === "Established") {
          setState("active");
          setStatusLine("Call active — you should hear the echo test.");
        } else if (s === "Terminating") {
          setStatusLine("Terminating…");
        } else if (s === "Terminated") {
          setState((prev) => (prev === "failed" ? prev : "ended"));
          setStatusLine("Call ended.");
        }
      });

      // Remote audio playback
      inviter.delegate = {
        onSessionDescriptionHandler: (sdh: any) => {
          const peer = sdh?.peerConnection;
          if (!peer) return;
          peer.getReceivers().forEach((receiver: any) => {
            if (receiver.track && audioRef.current) {
              audioRef.current.srcObject = new MediaStream([receiver.track]);
              audioRef.current.play().catch(() => { /* autoplay guard */ });
            }
          });
        },
      };

      await inviter.invite();
    } catch (e: any) {
      setState("failed");
      setError(e?.message || "Call failed");
    }
  }, [wssUri, target, state]);

  const hangUp = useCallback(async () => {
    try {
      const s = sessionRef.current as any;
      if (s) {
        if (s.state === "Established") await s.bye();
        else await s.cancel?.() ?? s.reject?.();
      }
    } catch { /* noop */ }
    setState("ended");
    setStatusLine("Hung up.");
  }, []);

  const stateChipColor =
    state === "active" ? "success" : state === "failed" ? "error" :
    state === "connecting" ? "warning" : "default";

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Test Call (WebRTC)</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Places a live call over WSS directly to FreeSWITCH's echo test (9196) —
        verifies signaling + media without touching carrier routes. Requires a
        browser with microphone permission only for the SDP offer; audio is echoed back.
      </Typography>

      <Card sx={{ maxWidth: 560, mb: 2 }}>
        <CardContent>
          <TextField
            fullWidth size="small" label="Media WSS URI"
            value={wssUri} onChange={(e) => setWssUri(e.target.value)}
            disabled={state === "connecting" || state === "active"}
            sx={{ mb: 2 }}
            helperText="Persisted in this browser (localStorage: fs_wss_uri)"
          />
          <TextField
            fullWidth size="small" label="Destination"
            value={target} onChange={(e) => setTarget(e.target.value)}
            disabled={state === "connecting" || state === "active"}
            sx={{ mb: 2 }}
            helperText="9196 = echo test, 9195 = delay echo"
          />

          <Box display="flex" gap={2} alignItems="center">
            {state !== "active" ? (
              <Button
                variant="contained" color="primary" startIcon={<CallIcon />}
                onClick={makeCall}
                disabled={state === "connecting"}
              >
                {state === "connecting" ? "Connecting…" : "Place test call"}
              </Button>
            ) : (
              <Button
                variant="contained" color="error" startIcon={<CallEndIcon />}
                onClick={hangUp}
              >
                Hang up
              </Button>
            )}
            <Chip label={state.toUpperCase()} color={stateChipColor as any} />
          </Box>

          {statusLine && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {statusLine}
            </Typography>
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          <audio ref={audioRef} autoPlay style={{ display: "none" }} />
        </CardContent>
      </Card>

      <Alert severity="info">
        Signaling goes to the media server WSS (:7443) — routed via the edge
        is a documented follow-up. Calls to 9196 are answered by the echo test
        and are NOT billed (default dialplan).
      </Alert>
    </Box>
  );
}
